import { Injectable } from "@angular/core";
import { Action, Selector, State, StateContext, Store, createSelector } from "@ngxs/store";
import { patch, removeItem, updateItem } from "@ngxs/store/operators";
import { of } from "rxjs";

import { AppDataStateModel } from "@app/shared/models/app-data.model";
import * as AppDataActions from "@app/shared/actions/app-data.action";
import * as GeneralActions from "@shared/actions/general.action";
import { WebApiService } from "@shared/services/web-api.service";
import { ActionTask, LongRunningTaskData, WebApiResponse } from "@shared/classes/general";
import { KpiITToolUsage } from "@shared/classes/kpi-it-tool-usage";
import {
  DMPRole,
  DateTimeFormatStrings,
  DriverDataBusinessType,
  HttpMethodEnum,
  LogicalOperator,
  Status,
  TaskDataRefreshStrategy,
  TaskStatusEnum,
  TeamMessageReceiverType,
} from "@shared/enums";
import { Constants, NgxsActionName, WebApiRoute } from "@shared/constants";
import { LongRunningTask } from "@shared/decorators/long-running-task.decorator";
import { TaskHelper, TaskManagerHelper } from "@shared/utils/task-manager.helper";
import { DefaultITToolUsage } from "@shared/models/it-tool-usage.model";
import { DrivingBehavior } from "@shared/classes/driving-behavior";
import { DefaultDrivingBehavior } from "@shared/models/driving-behavior.model";
import { DefaultKpiEconomy } from "@shared/models/kpi-economy.model";
import { KpiEconomy } from "@shared/classes/kpi-economy";
import { Utilities } from "@shared/utils/utilities";
import { DefaultKpiOthers } from "@shared/models/kpi-others.model";
import { KpiOthers } from "@shared/classes/kpi-others";
import { DefaultCoaching } from "@shared/models/coaching.model";
import { Coaching } from "@shared/classes/coaching";
import { AdvancedQueryStringGenerateor } from "@shared/classes/smart-search/advanced-query-string-generator";
import {
  KpiDrivingBehaviorQueryCriteriaGenerator,
  KpiOthersQueryCriteriaGenerator,
  CoachingQueryCriteriaGenerator,
  KpiSafetyQueryCriteriaGenerator,
  AchievementQueryCriteriaGenerator,
  ReportLinkQueryCriteriaGenerator,
  KpiITToolUsageQueryCriteriaGenerator,
  UserProfileQueryCriteriaGenerator,
  KpiEconomyQueryCriteriaGenerator,
  AwardQueryCriteriaGenerator,
  KpiConfigurationQueryCriteriaGenerator,
  DriverDataQueryCriteriaGenerator,
} from "@shared/classes/smart-search/query-string-generator.implementor";
import { KpiSafety } from "@shared/classes/kpi-safety";
import { DefaultSafety } from "@shared/models/safety.model";
import { DefaultDriverData } from "@shared/models/driver-data.model";
import { DefaultAchievementModel } from "@shared/models/achievement.model";
import { Achievement } from "@shared/classes/achievement";
import { DefaultReportLinkModel } from "@shared/models/report-link.model";
import { ReportLink } from "@shared/classes/report-link";
import { DefaultOrgUnit } from "@shared/models/org-unit.model";
import { OrgUnit } from "@shared/classes/org-unit";
import { DefaultUserProfile } from "@shared/models/user-profile.model";
import { DefaultTeamMessageModel } from "@shared/models/team-message.model";
import { TeamMessage, TeamMessageRead } from "@shared/classes/team-message";
import { UserProfile } from "@app/shared/classes/user-profile";
import { Driver } from "@shared/classes/driver";
import { DefaultKpiConfigurationModel } from "@shared/models/kpi-configuration.model";
import { KpiConfiguration } from "@shared/classes/kpi-weight-management";
import { CommonDataStateModel, CommonSearchCriteria, CreateCommonSearchContext, GenerateLastModifySearchCriteria } from "@shared/models/common-data.model";
import { NgxsStoreHelpers } from "./store.helpers";
import { TeamMessageFieldName } from "@app/pages/team-message/team-message.constant";
import { DefaultAwardModel } from "@shared/models/award.model";
import { Award } from "@shared/classes/award";
import { StateProcessorFactory } from "./state-processor/state-processor";
import { DefaultSupervisorControlPanel } from "@shared/models/supervisor-control-panel.model";
import { environment } from "@env/environment";
import { SiteConfigHelper } from "@shared/utils/site-config.helper";
import { DataImportValidateRequest } from "@shared/classes/data-import-validate-request";
import { Terminal } from "@shared/classes/terminal";

@State<AppDataStateModel>({
  name: "appDataState",
  defaults: {
    taskManager: [],
    commonPageSettings: [],
    pageFilterSettings: [],
    orgUnit: DefaultOrgUnit,
    driverData: DefaultDriverData,
    userProfile: DefaultUserProfile,
    itToolUsage: DefaultITToolUsage,
    safety: DefaultSafety,
    drivingBehavior: DefaultDrivingBehavior,
    kpiEconomy: DefaultKpiEconomy,
    kpiOthers: DefaultKpiOthers,
    coaching: DefaultCoaching,
    achievement: DefaultAchievementModel,
    award: DefaultAwardModel,
    reportLink: DefaultReportLinkModel,
    externalReport: { search: CreateCommonSearchContext() },
    teamMessage: DefaultTeamMessageModel,
    kpiConfiguration: DefaultKpiConfigurationModel,
    supervisorControlPanel: DefaultSupervisorControlPanel,
  },
})
@Injectable()
export class AppDataState {
  constructor(private store: Store, private webApiService: WebApiService) {}

  // #region Long Running Task
  @Action(GeneralActions.RegisterTask)
  RegisterTask(ctx: StateContext<AppDataStateModel>, action: GeneralActions.RegisterTask) {
    var currentTasks = ctx.getState().taskManager;
    //Just in case the exact same Task Id has been triggerred multiple times.
    //We will not register it duplicately
    var taskIdExists = currentTasks.some((t) => t.taskId === action.task.taskId);
    if (!taskIdExists) {
      if (action.task.taskData.refreshStrategy === TaskDataRefreshStrategy.CLEANUP) {
        //Clean up the 'completed' and 'failed' task for same NGXS Action
        currentTasks = currentTasks.filter((t) => t.taskName != action.task.taskName || TaskHelper.IsTaskInExecutingStatus(t));
        //Add new task for current NGXS Action
        currentTasks = [...currentTasks, action.task];
      } else {
        //Inline override the existing task with same task name, only keep the response from last time's task
        var targetTask = currentTasks.find((t) => t.taskName == action.task.taskName);
        if (targetTask) {
          if (TaskHelper.IsTaskInCompletedStatus(targetTask)) {
            currentTasks = currentTasks.map((t) => (t.taskName === action.task.taskName ? { ...action.task, response: t.response } : t));
          }
        } else {
          //Add new task for current NGXS Action
          currentTasks = [...currentTasks, action.task];
        }
      }
      ctx.setState(patch({ taskManager: currentTasks }));
    }
  }

  @Action(GeneralActions.ResumeTask)
  ResumeTask(ctx: StateContext<AppDataStateModel>, action: GeneralActions.ResumeTask) {
    ctx.setState(
      patch({
        taskManager: updateItem<ActionTask>((i) => i.taskId === action.taskId, patch({ status: TaskStatusEnum.RESUMING.toString() })),
      })
    );
  }

  @Action(GeneralActions.FinishTask)
  FinishTask(ctx: StateContext<AppDataStateModel>, action: GeneralActions.FinishTask) {
    let patchTask = { status: TaskStatusEnum.COMPLETED.toString() } as ActionTask;
    if (action.taskResponse) {
      patchTask = { ...patchTask, response: action.taskResponse };
    }
    ctx.setState(
      patch({
        taskManager: updateItem<ActionTask>((i) => i.taskId === action.taskId, patch(patchTask)),
      })
    );
  }

  @Action(GeneralActions.FailTask)
  FailTask(ctx: StateContext<AppDataStateModel>, action: GeneralActions.FailTask) {
    ctx.setState(
      patch({
        taskManager: updateItem<ActionTask>((i) => i.taskId === action.taskId, patch({ status: TaskStatusEnum.FAILED.toString(), response: null, error: action.error })),
      })
    );
  }

  @Action(GeneralActions.TerminateTask)
  TerminateTask(ctx: StateContext<AppDataStateModel>, action: GeneralActions.TerminateTask) {
    ctx.setState(
      patch({
        taskManager: removeItem<ActionTask>((i) => i.taskName === action.taskName && (!action.taskId || i.taskId === action.taskId) && TaskHelper.IsTaskInCompletedStatus(i)),
      })
    );
  }

  @Action(GeneralActions.MarkTaskTimeout)
  MarkTaskTimeout(ctx: StateContext<AppDataStateModel>, action: GeneralActions.MarkTaskTimeout) {
    ctx.setState(
      patch({
        taskManager: updateItem<ActionTask>((i) => i.taskId === action.taskId, patch({ status: TaskStatusEnum.TIMEOUT.toString() })),
      })
    );
    const task = ctx.getState().taskManager.find((t) => t.taskId === action.taskId);
    if (task && task.backgroundFinished && task.taskData) {
      ctx.dispatch(task.taskData);
    }
  }

  static taskExecuting(action: string) {
    return createSelector([AppDataState], (state: AppDataStateModel) => TaskManagerHelper.IsTaskExecuting(state.taskManager, action));
  }

  static isTaskSuccess(action: string) {
    return createSelector([AppDataState], (state: AppDataStateModel) => TaskManagerHelper.IsTaskExecuting(state.taskManager, action));
  }

  static taskResponse(taskName: string) {
    return createSelector([AppDataState], (state: AppDataStateModel) => {
      return TaskManagerHelper.GetTaskResponse(state.taskManager, taskName);
    });
  }

  static tasksExecuting(actions: string[], opr: LogicalOperator) {
    return createSelector([AppDataState], (state: AppDataStateModel) => {
      var results = actions.map((action) => TaskManagerHelper.IsTaskExecuting(state.taskManager, action));
      return opr === LogicalOperator.AND ? results.every(Boolean) : results.some(Boolean);
    });
  }

  static tasksSuccessed(actions: string[], opr: LogicalOperator) {
    return createSelector([AppDataState], (state: AppDataStateModel) => {
      var results = actions.map((action) => TaskManagerHelper.IsTaskSuccessed(state.taskManager, action));
      return opr === LogicalOperator.AND ? results.every(Boolean) : results.some(Boolean);
    });
  }

  static taskError(action: string) {
    return createSelector([AppDataState], (state: AppDataStateModel) => {
      var results = TaskManagerHelper.GetTaskError(state.taskManager, action);
      return results;
    });
  }

  static tasksError(actions: string[]) {
    return createSelector([AppDataState], (state: AppDataStateModel) => {
      var results = actions.map((action) => TaskManagerHelper.GetTaskError(state.taskManager, action));
      return results.filter((item) => item !== undefined);
    });
  }
  // #endregion Long Running Task

  // #region Driver Data
  @Selector()
  static currentDriverDataSearchBy(state: AppDataStateModel) {
    return state.driverData.search.searchForm.model.searchCriteria ?? [];
  }

  @LongRunningTask
  @Action(AppDataActions.GetDriverData)
  GetDriverData(ctx: StateContext<AppDataStateModel>, action: AppDataActions.GetDriverData, taskData: LongRunningTaskData) {
    var searchCriteria = ctx.getState().driverData.search.searchForm.model.searchCriteria;
    var pageFilterInfo = NgxsStoreHelpers.UpdatePageFilterSettingIntoStore(ctx, action.setting, NgxsActionName.GET_DRIVER_DATA);
    const queryString = new AdvancedQueryStringGenerateor(new DriverDataQueryCriteriaGenerator()).CreateQueryString(
      searchCriteria,
      pageFilterInfo,
      (<CommonDataStateModel>this.store.selectSnapshot((state) => state.commonDataState)).profileMe.country
    );

    return this.webApiService.request<WebApiResponse<Driver[]>>(HttpMethodEnum.GET, `${WebApiRoute.DRIVER_DATA}${queryString}`, null, taskData);
  }

  @LongRunningTask
  @Action(AppDataActions.UpdateDriverData)
  UpdateDriverData(ctx: StateContext<AppDataStateModel>, action: AppDataActions.UpdateDriverData, taskData: LongRunningTaskData) {
    const model = ctx.getState().driverData.edit.editForm.model;
    var terminals = TaskManagerHelper.GetTaskResponse(ctx.getState().taskManager, NgxsActionName.GET_DRIVER_RELATED_TERMINAL_LIST) as Terminal[];
    const body: any = {
      terminal: {
        terminal_id: model.terminal_Id,
      },
      businessType: terminals.filter((terminal) => terminal.terminal_Id === model.terminal_Id)[0].businessType,
    };

    return this.webApiService.request<number>(HttpMethodEnum.PUT, `${WebApiRoute.DRIVER_DATA}/${model.driver_Id}`, body, taskData);
  }
  // #endregion Driver Data

  // #region Org Unit
  @Selector()
  static currentOrgUnitSearchBy(state: AppDataStateModel) {
    return state.orgUnit.search.searchForm.model.searchCriteria;
  }

  @LongRunningTask
  @Action(AppDataActions.GetTerminalsWithoutSubregion)
  GetTerminalsWithoutSubregion(ctx: StateContext<AppDataStateModel>, action: AppDataActions.GetTerminalsWithoutSubregion, taskData: LongRunningTaskData) {
    return this.webApiService.request<Terminal[]>(HttpMethodEnum.GET, `${WebApiRoute.ORG_UNIT_TERMINALS_WITHOUT_SUBREGION}`, null, taskData);
  }

  @LongRunningTask
  @Action(AppDataActions.GetOrgUnit)
  GetOrgUnit(ctx: StateContext<AppDataStateModel>, action: AppDataActions.GetOrgUnit, taskData: LongRunningTaskData) {
    var searchCriteria = ctx.getState().orgUnit.search.searchForm.model.searchCriteria;
    var pageFilterInfo = NgxsStoreHelpers.UpdatePageFilterSettingIntoStore(ctx, action.setting, NgxsActionName.GET_ORG_UNIT);
    const queryString = new AdvancedQueryStringGenerateor().CreateQueryString(
      searchCriteria,
      pageFilterInfo,
      (<CommonDataStateModel>this.store.selectSnapshot((state) => state.commonDataState)).profileMe.country
    );
    return this.webApiService.request<WebApiResponse<OrgUnit[]>>(HttpMethodEnum.GET, `${WebApiRoute.ORG_UNIT}${queryString}`, null, taskData);
  }

  @LongRunningTask
  @Action(AppDataActions.UpdateOrgUnit)
  UpdateOrgUnit(ctx: StateContext<AppDataStateModel>, action: AppDataActions.UpdateOrgUnit, taskData: LongRunningTaskData) {
    const model = ctx.getState().orgUnit.edit.editForm.model;
    const body: any = {
      terminal_id: model.terminal_id,
      subregion_id: model.subregion_id,
    };

    return this.webApiService.request<number>(HttpMethodEnum.POST, `${WebApiRoute.ORG_UNIT}`, body, taskData);
  }

  @LongRunningTask
  @Action(AppDataActions.DeleteOrgUnit)
  DeleteOrgUnit(ctx: StateContext<AppDataStateModel>, action: AppDataActions.DeleteOrgUnit, taskData: LongRunningTaskData) {
    return this.webApiService.request<WebApiResponse<OrgUnit[]>>(HttpMethodEnum.DELETE, `${WebApiRoute.ORG_UNIT_TERMINALS}`, action.deleteIdList, taskData);
  }

  // #endregion Org Unit

  // #region User Profile
  @Selector()
  static currentUserProfileSearchBy(state: AppDataStateModel) {
    return state.userProfile.search.searchForm.model.searchCriteria;
  }

  @LongRunningTask
  @Action(AppDataActions.GetUserProfile)
  GetUserProfile(ctx: StateContext<AppDataStateModel>, action: AppDataActions.GetUserProfile, taskData: LongRunningTaskData) {
    var searchCriteria = ctx.getState().userProfile.search.searchForm.model.searchCriteria;
    var pageFilterInfo = NgxsStoreHelpers.UpdatePageFilterSettingIntoStore(ctx, action.setting, NgxsActionName.GET_USER_PROFILE);
    var profileMe = (<CommonDataStateModel>this.store.selectSnapshot((state) => state.commonDataState)).profileMe;
    const queryString = new AdvancedQueryStringGenerateor(new UserProfileQueryCriteriaGenerator()).CreateQueryString(
      searchCriteria,
      pageFilterInfo,
      profileMe.role_id === DMPRole.SYSTEM_ADMIN ? null : profileMe.country
    );
    return this.webApiService.request<WebApiResponse<UserProfile[]>>(HttpMethodEnum.GET, `${WebApiRoute.USER_PROFILE}${queryString}`, null, taskData);
  }

  @LongRunningTask
  @Action(AppDataActions.UpdateUserProfile)
  UpdateUserProfile(ctx: StateContext<AppDataStateModel>, action: AppDataActions.UpdateUserProfile, taskData: LongRunningTaskData) {
    const model = ctx.getState().userProfile.edit.editForm.model;
    const isCreate = model.userProfile_Id === -1;
    const body: any = {
      apexId: model.apexId,
      role: model.role,
      lastName: model.userName.split(",")[0]?.trim(),
      firstName: model.userName.split(",")[1]?.trim(),
      country: model.country,
      subRegion_Ids: model.subRegion_Id ? [model.subRegion_Id] : [],
      terminal_Ids: model.terminal_Ids,
      businessTypes: model.businessTypes === DriverDataBusinessType.PG ? [DriverDataBusinessType.PG] : [DriverDataBusinessType.LB, DriverDataBusinessType.MB],
      status: Status.ACTIVE,
    };

    if (isCreate) {
      return this.webApiService.request<number>(HttpMethodEnum.POST, `${WebApiRoute.USER_PROFILE}`, body, taskData);
    } else {
      return this.webApiService.request<number>(HttpMethodEnum.PUT, `${WebApiRoute.USER_PROFILE}/${model.userProfile_Id}`, body, taskData);
    }
  }

  @LongRunningTask
  @Action(AppDataActions.DeleteUserProfile)
  DeleteUserProfile(ctx: StateContext<AppDataStateModel>, action: AppDataActions.DeleteUserProfile, taskData: LongRunningTaskData) {
    return this.webApiService.request<WebApiResponse<ReportLink[]>>(HttpMethodEnum.DELETE, `${WebApiRoute.USER_PROFILE}`, action.deleteIdList, taskData);
  }

  // #endregion User Profile

  // #region Kpi IT Tools Usage
  @Selector()
  static currentItToolUsageSearchBy(state: AppDataStateModel) {
    return state.itToolUsage.search.searchForm.model.searchCriteria;
  }

  @LongRunningTask
  @Action(AppDataActions.GetKpiITToolUsage)
  GetKpiITToolUsage(ctx: StateContext<AppDataStateModel>, action: AppDataActions.GetKpiITToolUsage, taskData: LongRunningTaskData) {
    var searchCriteria = ctx.getState().itToolUsage.search.searchForm.model.searchCriteria;
    var pageFilterInfo = NgxsStoreHelpers.UpdatePageFilterSettingIntoStore(ctx, action.setting, NgxsActionName.GET_KPI_IT_TOOL_USAGE);
    const queryString = new AdvancedQueryStringGenerateor(new KpiITToolUsageQueryCriteriaGenerator()).CreateQueryString(
      searchCriteria,
      pageFilterInfo,
      (<CommonDataStateModel>this.store.selectSnapshot((state) => state.commonDataState)).profileMe.country
    );
    return this.webApiService.request<WebApiResponse<KpiITToolUsage[]>>(HttpMethodEnum.GET, `${WebApiRoute.KPI_IT_TOOL_USAGE}${queryString}`, null, taskData);
  }

  @LongRunningTask
  @Action(AppDataActions.UpdateKpiITToolUsage)
  UpdateKpiITToolUsage(ctx: StateContext<AppDataStateModel>, action: AppDataActions.UpdateKpiITToolUsage, taskData: LongRunningTaskData) {
    var model = ctx.getState().itToolUsage.edit.editForm.model;

    const body = SiteConfigHelper.IsOccurrenceTargetEditableKPIs(model.kpiId)
      ? {
          compliance: model.compliance,
          occurrence: model.occurrence,
          targetOccurrence: model.targetOccurrence,
        }
      : {
          compliance: model.compliance,
        };
    return this.webApiService.request<number>(HttpMethodEnum.PUT, `${WebApiRoute.KPI_IT_TOOL_USAGE}/${action.data.kpiITToolUsage_Id}`, body, taskData);
  }

  @LongRunningTask
  @Action(AppDataActions.ImportKpiITToolUsageData)
  ImportKpiITToolUsage(ctx: StateContext<AppDataStateModel>, action: AppDataActions.ImportKpiITToolUsageData, taskData: LongRunningTaskData) {
    return this.webApiService.request(HttpMethodEnum.POST, `${WebApiRoute.KPI_IT_TOOL_USAGE}`, action.importData, taskData);
  }

  @LongRunningTask
  @Action(AppDataActions.ImportEVCRComplianceData)
  ImportEVCRComplianceData(ctx: StateContext<AppDataStateModel>, action: AppDataActions.ImportEVCRComplianceData, taskData: LongRunningTaskData) {
    return this.webApiService.request(HttpMethodEnum.POST, `${WebApiRoute.KPI_IT_TOOL_USAGE}`, action.importData, taskData);
  }

  @LongRunningTask
  @Action(AppDataActions.ImportAPTComplianceData)
  ImportAPTComplianceData(ctx: StateContext<AppDataStateModel>, action: AppDataActions.ImportAPTComplianceData, taskData: LongRunningTaskData) {
    return this.webApiService.request(HttpMethodEnum.POST, `${WebApiRoute.KPI_IT_TOOL_USAGE}`, action.importData, taskData);
  }

  @LongRunningTask
  @Action(AppDataActions.ImportSaftyMeetingComplianceData)
  ImportSaftyMeetingComplianceData(ctx: StateContext<AppDataStateModel>, action: AppDataActions.ImportSaftyMeetingComplianceData, taskData: LongRunningTaskData) {
    return this.webApiService.request(HttpMethodEnum.POST, `${WebApiRoute.KPI_IT_TOOL_USAGE}`, action.importData, taskData);
  }

  @LongRunningTask
  @Action(AppDataActions.DeleteKpiITToolUsage)
  DeleteKpiITToolUsage(ctx: StateContext<AppDataStateModel>, action: AppDataActions.DeleteKpiITToolUsage, taskData: LongRunningTaskData) {
    return this.webApiService.request<WebApiResponse<KpiITToolUsage[]>>(HttpMethodEnum.DELETE, `${WebApiRoute.KPI_IT_TOOL_USAGE}`, action.deleteIdList, taskData);
  }
  // #endregion Kpi IT Tools Usage

  // #region Kpi Safety
  @Selector()
  static currentSafetySearchBy(state: AppDataStateModel) {
    return state.safety.search.searchForm.model.searchCriteria;
  }

  @LongRunningTask
  @Action(AppDataActions.GetKpiSafety)
  GetKpiSafety(ctx: StateContext<AppDataStateModel>, action: AppDataActions.GetKpiSafety, taskData: LongRunningTaskData) {
    var searchCriteria = ctx.getState().safety.search.searchForm.model.searchCriteria;
    var pageFilterInfo = NgxsStoreHelpers.UpdatePageFilterSettingIntoStore(ctx, action.setting, NgxsActionName.GET_KPI_SAFETY);
    const queryString = new AdvancedQueryStringGenerateor(new KpiSafetyQueryCriteriaGenerator()).CreateQueryString(
      searchCriteria,
      pageFilterInfo,
      (<CommonDataStateModel>this.store.selectSnapshot((state) => state.commonDataState)).profileMe.country
    );
    return this.webApiService.request<WebApiResponse<KpiSafety[]>>(HttpMethodEnum.GET, `${WebApiRoute.KPI_SAFETY}${queryString}`, null, taskData);
  }

  @LongRunningTask
  @Action(AppDataActions.UpdateKpiSafety)
  UpdateKpiSafety(ctx: StateContext<AppDataStateModel>, action: AppDataActions.UpdateKpiSafety, taskData: LongRunningTaskData) {
    const body = {
      status: ctx.getState().safety.edit.editForm.model.status,
      kpisaftycomment: ctx.getState().safety.edit.editForm.model.kpiSaftyComment,
    };
    return this.webApiService.request<number>(HttpMethodEnum.PUT, `${WebApiRoute.KPI_SAFETY}/${action.data.kpiSafty_Id}`, body, taskData);
  }
  // #endregion Kpi Safety

  // #region Kpi Driving Behavior
  @Selector()
  static currentDrivingBehaviorSearchBy(state: AppDataStateModel) {
    return state.drivingBehavior.search.searchForm.model.searchCriteria;
  }

  @LongRunningTask
  @Action(AppDataActions.GetKpiDrivingBehavior)
  GetKpiDrivingBehavior(ctx: StateContext<AppDataStateModel>, action: AppDataActions.GetKpiDrivingBehavior, taskData: LongRunningTaskData) {
    var searchCriteria = ctx.getState().drivingBehavior.search.searchForm.model.searchCriteria;
    var pageFilterInfo = NgxsStoreHelpers.UpdatePageFilterSettingIntoStore(ctx, action.setting, NgxsActionName.GET_KPI_DRIVING_BEHAVIOR);
    const queryString = new AdvancedQueryStringGenerateor(new KpiDrivingBehaviorQueryCriteriaGenerator()).CreateQueryString(
      searchCriteria,
      pageFilterInfo,
      (<CommonDataStateModel>this.store.selectSnapshot((state) => state.commonDataState)).profileMe.country
    );
    return this.webApiService.request<WebApiResponse<DrivingBehavior[]>>(HttpMethodEnum.GET, `${WebApiRoute.KPI_DRIVING_BEHAVIOR}${queryString}`, null, taskData);
  }

  @LongRunningTask
  @Action(AppDataActions.UpdateKpiDrivingBehavior)
  UpdateKpiDrivingBehavior(ctx: StateContext<AppDataStateModel>, action: AppDataActions.UpdateKpiDrivingBehavior, taskData: LongRunningTaskData) {
    var model = ctx.getState().drivingBehavior.edit.editForm.model;
    const body = {
      compliance: model.compliance,
      description: model.description,
    };

    return this.webApiService.request<number>(HttpMethodEnum.PUT, `${WebApiRoute.KPI_DRIVING_BEHAVIOR}/${action.data.kpiDrivingBehavior_Id}`, body, taskData);
  }

  @LongRunningTask
  @Action(AppDataActions.DeleteKpiDrivingBehavior)
  DeleteKpiDrivingBehavior(ctx: StateContext<AppDataStateModel>, action: AppDataActions.DeleteKpiDrivingBehavior, taskData: LongRunningTaskData) {
    return this.webApiService.request<WebApiResponse<DrivingBehavior[]>>(HttpMethodEnum.DELETE, `${WebApiRoute.KPI_DRIVING_BEHAVIOR}`, action.deleteIdList, taskData);
  }

  @LongRunningTask
  @Action(AppDataActions.ImportKpiDrivingBehaviorData)
  ImportKpiDrivingBehaviorData(ctx: StateContext<AppDataStateModel>, action: AppDataActions.ImportKpiDrivingBehaviorData, taskData: LongRunningTaskData) {
    return this.webApiService.request(HttpMethodEnum.POST, `${WebApiRoute.KPI_DRIVING_BEHAVIOR}`, action.importData, taskData);
  }
  // #endregion Kpi Driving Behavior

  // #region Kpi Economy
  @Selector()
  static currentKpiEconomySearchBy(state: AppDataStateModel) {
    return state.kpiEconomy.search.searchForm.model.searchCriteria;
  }

  @LongRunningTask
  @Action(AppDataActions.GetKpiEconomy)
  GetKpiEconomy(ctx: StateContext<AppDataStateModel>, action: AppDataActions.GetKpiEconomy, taskData: LongRunningTaskData) {
    var searchCriteria = ctx.getState().kpiEconomy.search.searchForm.model.searchCriteria;
    var pageFilterInfo = NgxsStoreHelpers.UpdatePageFilterSettingIntoStore(ctx, action.setting, NgxsActionName.GET_KPI_ECONOMY);
    const queryString = new AdvancedQueryStringGenerateor(new KpiEconomyQueryCriteriaGenerator()).CreateQueryString(
      searchCriteria,
      pageFilterInfo,
      (<CommonDataStateModel>this.store.selectSnapshot((state) => state.commonDataState)).profileMe.country
    );
    return this.webApiService.request<WebApiResponse<KpiEconomy[]>>(HttpMethodEnum.GET, `${WebApiRoute.KPI_ECONOMY}${queryString}`, null, taskData);
  }

  @LongRunningTask
  @Action(AppDataActions.UpdateKpiEconomy)
  UpdateKpiEconomy(ctx: StateContext<AppDataStateModel>, action: AppDataActions.UpdateKpiEconomy, taskData: LongRunningTaskData) {
    const body: any = {
      apexid: action.data.driver.apexId.toLocaleLowerCase(),
      kpiid: action.data.kpi.kpiId,
      kpidate: Utilities.formatDate(new Date(action.data.kpiDate), DateTimeFormatStrings.FORMAT_1),
    };

    const kpiValue = ctx.getState().kpiEconomy.edit.editForm.model.kpiValue;
    if (kpiValue !== undefined) {
      body.kpivalue = kpiValue;
    } else {
      body.kpiscore = ctx.getState().kpiEconomy.edit.editForm.model.kpiScore;
    }

    return this.webApiService.request<number>(HttpMethodEnum.PUT, `${WebApiRoute.KPI_ECONOMY}/${action.data.kpiEconomy_Id}`, body, taskData);
  }

  @LongRunningTask
  @Action(AppDataActions.DeleteKpiEconomy)
  DeleteKpiEconomy(ctx: StateContext<AppDataStateModel>, action: AppDataActions.DeleteKpiEconomy, taskData: LongRunningTaskData) {
    return this.webApiService.request<WebApiResponse<KpiEconomy[]>>(HttpMethodEnum.DELETE, `${WebApiRoute.KPI_ECONOMY}`, action.deleteIdList, taskData);
  }

  @LongRunningTask
  @Action(AppDataActions.ImportKpiEconomyData)
  ImportKpiEconomyData(ctx: StateContext<AppDataStateModel>, action: AppDataActions.ImportKpiEconomyData, taskData: LongRunningTaskData) {
    return this.webApiService.request(HttpMethodEnum.POST, `${WebApiRoute.KPI_ECONOMY}`, action.importData, taskData);
  }
  // #endregion Kpi Economy

  // #region Kpi Others
  @Selector()
  static currentKpiOthersSearchBy(state: AppDataStateModel) {
    return state.kpiOthers.search.searchForm.model.searchCriteria;
  }

  @LongRunningTask
  @Action(AppDataActions.GetKpiOthers)
  GetKpiOthers(ctx: StateContext<AppDataStateModel>, action: AppDataActions.GetKpiOthers, taskData: LongRunningTaskData) {
    var searchCriteria = ctx.getState().kpiOthers.search.searchForm.model.searchCriteria;
    var pageFilterInfo = NgxsStoreHelpers.UpdatePageFilterSettingIntoStore(ctx, action.setting, NgxsActionName.GET_KPI_OTHERS);
    const queryString = new AdvancedQueryStringGenerateor(new KpiOthersQueryCriteriaGenerator()).CreateQueryString(
      searchCriteria,
      pageFilterInfo,
      (<CommonDataStateModel>this.store.selectSnapshot((state) => state.commonDataState)).profileMe.country
    );
    return this.webApiService.request<WebApiResponse<KpiOthers[]>>(HttpMethodEnum.GET, `${WebApiRoute.KPI_OTHERS}${queryString}`, null, taskData);
  }

  @LongRunningTask
  @Action(AppDataActions.GetKpiOthersAttachment)
  GetKpiOthersAttachment(ctx: StateContext<AppDataStateModel>, action: AppDataActions.GetKpiOthersAttachment, taskData: LongRunningTaskData) {
    return this.webApiService.requestRawData(HttpMethodEnum.GET, true, `${WebApiRoute.KPI_OTHERS}/${action.id}/attachment`, null, taskData, "arraybuffer");
  }

  @LongRunningTask
  @Action(AppDataActions.UpdateKpiOthers)
  UpdateKpiOthers(ctx: StateContext<AppDataStateModel>, action: AppDataActions.UpdateKpiOthers, taskData: LongRunningTaskData) {
    const model = ctx.getState().kpiOthers.edit.editForm.model;
    const isCreate = model.kpiOthers_Id === -1;
    const body: any = {
      kpi: { kpi_id: model.kpi_Id },
      driver: { driver_id: model.driver_Id },
      title: model.title,
      description: model.description,
      attachment: model.attachment,
      kpitime: Utilities.formatDate(new Date(model.kpiTime), DateTimeFormatStrings.FORMAT_10),
    };

    if (isCreate) {
      return this.webApiService.request<number>(HttpMethodEnum.POST, `${WebApiRoute.KPI_OTHERS}`, body, taskData);
    } else {
      return this.webApiService.request<number>(HttpMethodEnum.PUT, `${WebApiRoute.KPI_OTHERS}/${model.kpiOthers_Id}`, body, taskData);
    }
  }

  @LongRunningTask
  @Action(AppDataActions.DeleteKpiOthers)
  DeleteKpiOthers(ctx: StateContext<AppDataStateModel>, action: AppDataActions.DeleteKpiOthers, taskData: LongRunningTaskData) {
    return this.webApiService.request<WebApiResponse<KpiOthers[]>>(HttpMethodEnum.DELETE, `${WebApiRoute.KPI_OTHERS}`, action.deleteIdList, taskData);
  }
  // #endregion Kpi Others

  // #region Coaching
  @Selector()
  static currentCoachingSearchBy(state: AppDataStateModel) {
    return state.coaching.search.searchForm.model.searchCriteria ?? [];
  }

  @LongRunningTask
  @Action(AppDataActions.GetCoaching)
  GetCoaching(ctx: StateContext<AppDataStateModel>, action: AppDataActions.GetCoaching, taskData: LongRunningTaskData) {
    var searchCriteria = ctx.getState().coaching.search.searchForm.model.searchCriteria;
    var pageFilterInfo = NgxsStoreHelpers.UpdatePageFilterSettingIntoStore(ctx, action.setting, NgxsActionName.GET_COACHING);
    var queryString = new AdvancedQueryStringGenerateor(new CoachingQueryCriteriaGenerator()).CreateQueryString(
      searchCriteria,
      pageFilterInfo,
      (<CommonDataStateModel>this.store.selectSnapshot((state) => state.commonDataState)).profileMe.country
    );
    // queryString = queryString.replace("apexId=", "apexId=%");
    return this.webApiService.request<WebApiResponse<Coaching[]>>(HttpMethodEnum.GET, `${WebApiRoute.COACHING}${queryString}`, null, taskData);
  }

  @LongRunningTask
  @Action(AppDataActions.GetCoachingAttachment)
  GetCoachingAttachment(ctx: StateContext<AppDataStateModel>, action: AppDataActions.GetCoachingAttachment, taskData: LongRunningTaskData) {
    return this.webApiService.requestRawData(HttpMethodEnum.GET, true, `${WebApiRoute.COACHING}/${action.id}/attachment`, null, taskData, "arraybuffer");
  }

  @LongRunningTask
  @Action(AppDataActions.UpdateCoaching)
  UpdateCoaching(ctx: StateContext<AppDataStateModel>, action: AppDataActions.UpdateCoaching, taskData: LongRunningTaskData) {
    const model = ctx.getState().coaching.edit.editForm.model;
    const isCreate = model.coaching_Id === -1;
    const body: any = {
      driver: { driver_id: model.driver_Id },
      coachingtime: Utilities.formatDate(new Date(model.coachingtime), DateTimeFormatStrings.FORMAT_10),
      mentorId: model.mentorId,
      title: model.abstract,
      coachingresult: model.coachingResult,
      attachment: model.attachment,
      attachmentVisibletoDriver: model.attachmentVisibletoDriver,
      emIncidentNumber: model.emIncidentNumber,
      status: model.status,
      mentorName: model.mentorName,
      driver3pl: model.driver3pl,
      driver3PLterminal_Id: model.driver3PLterminal_Id,
      driver3PLflag: model.driver3PLflag,
    };

    if (isCreate) {
      return this.webApiService.request<number>(HttpMethodEnum.POST, `${WebApiRoute.COACHING}`, body, taskData);
    } else {
      return this.webApiService.request<number>(HttpMethodEnum.PUT, `${WebApiRoute.COACHING}/${model.coaching_Id}`, body, taskData);
    }
  }
  // #endregion Coaching

  // #region Achievement
  @Selector()
  static currentAchievementSearchBy(state: AppDataStateModel) {
    return state.achievement.search.searchForm.model.searchCriteria ?? [];
  }

  @LongRunningTask
  @Action(AppDataActions.GetAchievement)
  GetAchievement(ctx: StateContext<AppDataStateModel>, action: AppDataActions.GetAchievement, taskData: LongRunningTaskData) {
    var searchCriteria = ctx.getState().achievement.search.searchForm.model.searchCriteria;
    var pageFilterInfo = NgxsStoreHelpers.UpdatePageFilterSettingIntoStore(ctx, action.setting, NgxsActionName.GET_ACHIEVEMENT);
    const queryString = new AdvancedQueryStringGenerateor(new AchievementQueryCriteriaGenerator()).CreateQueryString(
      searchCriteria,
      pageFilterInfo,
      (<CommonDataStateModel>this.store.selectSnapshot((state) => state.commonDataState)).profileMe.country
    );
    return this.webApiService.request<WebApiResponse<Achievement[]>>(HttpMethodEnum.GET, `${WebApiRoute.ACHIEVEMENT}${queryString}`, null, taskData);
  }

  @LongRunningTask
  @Action(AppDataActions.ImportAchievementData)
  ImportAchievementData(ctx: StateContext<AppDataStateModel>, action: AppDataActions.ImportAchievementData, taskData: LongRunningTaskData) {
    return this.webApiService.request(HttpMethodEnum.POST, `${WebApiRoute.ACHIEVEMENT}`, action.importData, taskData);
  }

  @LongRunningTask
  @Action(AppDataActions.UpdateAchievement)
  UpdateAchievement(ctx: StateContext<AppDataStateModel>, action: AppDataActions.UpdateAchievement, taskData: LongRunningTaskData) {
    var model = ctx.getState().achievement.edit.editForm.model;
    const body = {
      achieve_Id: model.achieve_Id,
      mileage: model.mileage,
      cylinder: model.cylinder,
      achieveDate: model.achieveDate,
    };

    return this.webApiService.request<number>(HttpMethodEnum.PUT, `${WebApiRoute.ACHIEVEMENT}/${model.achieve_Id}`, body, taskData);
  }
  // #endregion Achievement

  // #region Award
  @Selector()
  static currentAwardSearchBy(state: AppDataStateModel) {
    return state.award.search.searchForm.model.searchCriteria ?? [];
  }

  @LongRunningTask
  @Action(AppDataActions.GetAward)
  GetAward(ctx: StateContext<AppDataStateModel>, action: AppDataActions.GetAward, taskData: LongRunningTaskData) {
    var searchCriteria = ctx.getState().award.search.searchForm.model.searchCriteria;
    var pageFilterInfo = NgxsStoreHelpers.UpdatePageFilterSettingIntoStore(ctx, action.setting, NgxsActionName.GET_AWARD);
    const queryString = new AdvancedQueryStringGenerateor(new AwardQueryCriteriaGenerator()).CreateQueryString(
      searchCriteria,
      pageFilterInfo,
      (<CommonDataStateModel>this.store.selectSnapshot((state) => state.commonDataState)).profileMe.country
    );
    return this.webApiService.request<WebApiResponse<Award[]>>(HttpMethodEnum.GET, `${WebApiRoute.AWARD}${queryString}`, null, taskData);
  }

  @LongRunningTask
  @Action(AppDataActions.ImportAwardData)
  ImportAwardData(ctx: StateContext<AppDataStateModel>, action: AppDataActions.ImportAwardData, taskData: LongRunningTaskData) {
    return this.webApiService.request(HttpMethodEnum.POST, `${WebApiRoute.AWARD_IMPORT}`, action.importData, taskData);
  }

  @LongRunningTask
  @Action(AppDataActions.UpdateAward)
  UpdateAward(ctx: StateContext<AppDataStateModel>, action: AppDataActions.UpdateAward, taskData: LongRunningTaskData) {
    const model = ctx.getState().award.edit.editForm.model;
    const isCreate = model.award_Id === -1;
    const body: any = {
      driver: { Driver_Id: model.driver_Id },
      awardName: model.awardName,
      awardDate: Utilities.formatDate(new Date(model.awardDate), DateTimeFormatStrings.FORMAT_6),
    };

    if (isCreate) {
      return this.webApiService.request<number>(HttpMethodEnum.POST, `${WebApiRoute.AWARD}`, body, taskData);
    } else {
      return this.webApiService.request<number>(HttpMethodEnum.PUT, `${WebApiRoute.AWARD}/${model.award_Id}`, body, taskData);
    }
  }

  @LongRunningTask
  @Action(AppDataActions.DeleteAward)
  DeleteAward(ctx: StateContext<AppDataStateModel>, action: AppDataActions.DeleteAward, taskData: LongRunningTaskData) {
    return this.webApiService.request<WebApiResponse<Award[]>>(HttpMethodEnum.DELETE, `${WebApiRoute.AWARD}`, action.deleteIdList, taskData);
  }
  // #endregion Award

  // #region Report Link
  @Selector()
  static currentReportLinkSearchBy(state: AppDataStateModel) {
    return state.reportLink.search.searchForm.model.searchCriteria;
  }

  @LongRunningTask
  @Action(AppDataActions.GetReportLink)
  GetReportLink(ctx: StateContext<AppDataStateModel>, action: AppDataActions.GetReportLink, taskData: LongRunningTaskData) {
    var searchCriteria = ctx.getState().reportLink.search.searchForm.model.searchCriteria;
    var pageFilterInfo = NgxsStoreHelpers.UpdatePageFilterSettingIntoStore(ctx, action.setting, NgxsActionName.GET_REPORT_LINK);
    const queryString = new AdvancedQueryStringGenerateor(new ReportLinkQueryCriteriaGenerator()).CreateQueryString(
      searchCriteria,
      pageFilterInfo,
      (<CommonDataStateModel>this.store.selectSnapshot((state) => state.commonDataState)).profileMe.country
    );
    return this.webApiService.request<WebApiResponse<ReportLink[]>>(HttpMethodEnum.GET, `${WebApiRoute.REPORT_LINK}${queryString}`, null, taskData);
  }

  @LongRunningTask
  @Action(AppDataActions.UpdateReportLink)
  UpdateReportLink(ctx: StateContext<AppDataStateModel>, action: AppDataActions.UpdateReportLink, taskData: LongRunningTaskData) {
    const model = ctx.getState().reportLink.edit.editForm.model;
    const isCreate = model.reportLink_Id === -1;
    const body: any = {
      reportName: model.reportName,
      reportUrl: model.reportUrl,
      description: model.description,
      country: model.country,
    };

    if (isCreate) {
      return this.webApiService.request<number>(HttpMethodEnum.POST, `${WebApiRoute.REPORT_LINK}`, body, taskData);
    } else {
      return this.webApiService.request<number>(HttpMethodEnum.PUT, `${WebApiRoute.REPORT_LINK}/${model.reportLink_Id}`, body, taskData);
    }
  }

  @LongRunningTask
  @Action(AppDataActions.DeleteReportLink)
  DeleteReportLink(ctx: StateContext<AppDataStateModel>, action: AppDataActions.DeleteReportLink, taskData: LongRunningTaskData) {
    return this.webApiService.request<WebApiResponse<ReportLink[]>>(HttpMethodEnum.DELETE, `${WebApiRoute.REPORT_LINK}`, action.deleteIdList, taskData);
  }
  // #endregion Report Link

  // #region External Report
  @Selector()
  static currentExternalReportSearchBy(state: AppDataStateModel) {
    return state.externalReport.search.searchForm.model.searchCriteria;
  }

  @LongRunningTask
  @Action(AppDataActions.GetExternalReport)
  GetExternalReport(ctx: StateContext<AppDataStateModel>, action: AppDataActions.GetExternalReport, taskData: LongRunningTaskData) {
    var searchCriteria = ctx.getState().externalReport.search.searchForm.model.searchCriteria;
    var pageFilterInfo = NgxsStoreHelpers.UpdatePageFilterSettingIntoStore(ctx, action.setting, NgxsActionName.GET_EXTERNAL_REPORT);
    const queryString = new AdvancedQueryStringGenerateor(new ReportLinkQueryCriteriaGenerator()).CreateQueryString(
      searchCriteria,
      pageFilterInfo,
      (<CommonDataStateModel>this.store.selectSnapshot((state) => state.commonDataState)).profileMe.country
    );
    return this.webApiService.request<WebApiResponse<ReportLink[]>>(HttpMethodEnum.GET, `${WebApiRoute.REPORT_LINK}${queryString}`, null, taskData);
  }
  // #endregion External Report

  // #region TeamMessage
  @Selector()
  static currentTeamMessageSearchBy(state: AppDataStateModel) {
    return state.teamMessage.search.searchForm.model.searchCriteria;
  }

  @LongRunningTask
  @Action(AppDataActions.GetTeamMessage)
  GetTeamMessage(ctx: StateContext<AppDataStateModel>, action: AppDataActions.GetTeamMessage, taskData: LongRunningTaskData) {
    var searchCriteria = ctx.getState().teamMessage.search.searchForm.model.searchCriteria;
    var pageFilterInfo = NgxsStoreHelpers.UpdatePageFilterSettingIntoStore(ctx, action.setting, NgxsActionName.GET_TEAM_MESSAGE);
    const queryString = new AdvancedQueryStringGenerateor().CreateQueryString(
      searchCriteria,
      pageFilterInfo,
      (<CommonDataStateModel>this.store.selectSnapshot((state) => state.commonDataState)).profileMe.country
    );
    return this.webApiService.request<WebApiResponse<TeamMessage[]>>(HttpMethodEnum.GET, `${WebApiRoute.TEAM_MESSAGE}${queryString}`, null, taskData);
  }

  @LongRunningTask
  @Action(AppDataActions.UpdateTeamMessage)
  UpdateTeamMessage(ctx: StateContext<AppDataStateModel>, action: AppDataActions.UpdateTeamMessage, taskData: LongRunningTaskData) {
    const model = ctx.getState().teamMessage.edit.editForm.model;
    const isCreate = model.teamMessage_Id === -1;
    const body = {
      title: model.title,
      body: model.body,
      attachment: model.attachment,
      status: isCreate ? Status.ACTIVE : model.status,
    } as TeamMessage;

    if (isCreate) {
      switch (model.teamMessageReceiverType) {
        case TeamMessageReceiverType.COUNTRY:
          body.countries = [model.country];
          break;

        case TeamMessageReceiverType.SUB_REGION:
          body.subRegion_Ids = [model.subRegion_Id];
          break;

        case TeamMessageReceiverType.TERMINAL:
          body.terminal_Ids = model.terminal_Ids;
          break;

        case TeamMessageReceiverType.DRIVER:
          body.driver_Ids = model.driver_Ids;
      }

      return this.webApiService.request<number>(HttpMethodEnum.POST, `${WebApiRoute.TEAM_MESSAGE}`, body, taskData);
    } else {
      return this.webApiService.request<number>(HttpMethodEnum.PUT, `${WebApiRoute.TEAM_MESSAGE}/${model.teamMessage_Id}`, body, taskData);
    }
  }

  @LongRunningTask
  @Action(AppDataActions.GetDriverByTerminal)
  GetDriverByTerminal(ctx: StateContext<AppDataStateModel>, action: AppDataActions.GetDriverByTerminal, taskData: LongRunningTaskData) {
    if (!action.terminal_Ids || action.terminal_Ids.length === 0) {
      return of({ data: [] } as WebApiResponse<Driver[]>);
    }

    const queryString = new AdvancedQueryStringGenerateor().CreateQueryString(
      action.terminal_Ids.map((item) => {
        return {
          searchBy: TeamMessageFieldName.terminal_id,
          searchValue: item.toString(),
        } as CommonSearchCriteria;
      }),
      null
    );

    return this.webApiService.request<WebApiResponse<Driver[]>>(HttpMethodEnum.GET, `${WebApiRoute.DRIVER_DATA}${queryString}`, null, taskData);
  }

  @LongRunningTask
  @Action(AppDataActions.GetTeamMessageRead)
  GetTeamMessageRead(ctx: StateContext<AppDataStateModel>, action: AppDataActions.GetTeamMessageRead, taskData: LongRunningTaskData) {
    var searchCriteria = ctx.getState().teamMessage.teamMessageReadSearch.searchForm.model.searchCriteria;
    const queryString = new AdvancedQueryStringGenerateor().CreateQueryString(searchCriteria, action.setting);
    return this.webApiService.request<WebApiResponse<TeamMessageRead[]>>(
      HttpMethodEnum.GET,
      `${WebApiRoute.TEAM_MESSAGE_READ}${queryString}&teammessage_id=${action.teamMessage_id}`,
      null,
      taskData
    );
  }

  @LongRunningTask
  @Action(AppDataActions.GetTeamMessageReadForExport)
  GetTeamMessageReadForExport(ctx: StateContext<AppDataStateModel>, action: AppDataActions.GetTeamMessageReadForExport, taskData: LongRunningTaskData) {
    // var searchCriteria = ctx.getState().teamMessage.teamMessageReadSearch.searchForm.model.searchCriteria;
    // const queryString = new AdvancedQueryStringGenerateor().CreateQueryString(searchCriteria, action.setting);
    return this.webApiService.request<WebApiResponse<TeamMessageRead[]>>(
      HttpMethodEnum.GET,
      `${WebApiRoute.TEAM_MESSAGE_READ}?teammessage_id=${action.teamMessage_id}`,
      null,
      taskData
    );
  }
  // #endregion TeamMessage

  // #region KpiConfiguration
  @Selector()
  static currentKpiConfigurationSearchBy(state: AppDataStateModel) {
    return state.kpiConfiguration.search.searchForm.model.searchCriteria;
  }

  @Action(AppDataActions.SetKpiConfigurationSearchValueOfCountry)
  SetKpiConfigurationSearchValueOfCountry(ctx: StateContext<AppDataStateModel>) {
    if (ctx.getState().kpiConfiguration.search.searchForm.model.searchCriteria[0].searchValue !== "") {
      return;
    }

    var commonDataStateModel = <CommonDataStateModel>this.store.selectSnapshot((state) => state.commonDataState);
    NgxsStoreHelpers.SetKpiConfigurationSearchValueOfCountry(ctx, commonDataStateModel.profileMe.country);
  }

  @LongRunningTask
  @Action(AppDataActions.GetKpiConfiguration)
  GetKpiConfiguration(ctx: StateContext<AppDataStateModel>, action: AppDataActions.GetKpiConfiguration, taskData: LongRunningTaskData) {
    var searchCriteria = ctx.getState().kpiConfiguration.search.searchForm.model.searchCriteria;
    const queryString = new AdvancedQueryStringGenerateor(new KpiConfigurationQueryCriteriaGenerator()).CreateQueryString(
      searchCriteria,
      null,
      (<CommonDataStateModel>this.store.selectSnapshot((state) => state.commonDataState)).profileMe.country
    );
    return this.webApiService.request<WebApiResponse<KpiConfiguration[]>>(HttpMethodEnum.GET, `${WebApiRoute.KPI_CONFIGURATION}${queryString}`, null, taskData);
  }

  @LongRunningTask
  @Action(AppDataActions.UpdateKpiConfiguration)
  UpdateKpiConfiguration(ctx: StateContext<AppDataStateModel>, action: AppDataActions.UpdateKpiConfiguration, taskData: LongRunningTaskData) {
    var model = ctx.getState().kpiConfiguration.edit.editForm.model;
    const body = [
      {
        kpiConfiguration_Id: model.kpiConfiguration_Id,
        weight: model.weight,
      },
    ];

    return this.webApiService.request<WebApiResponse<KpiConfiguration[]>>(HttpMethodEnum.POST, `${WebApiRoute.KPI_CONFIGURATION}`, body, taskData);
  }
  // #endregion KpiConfiguration

  // #region Supervisor Control Panel
  @LongRunningTask
  @Action(AppDataActions.CommandInvoke)
  CommandInvoke(ctx: StateContext<AppDataStateModel>, action: AppDataActions.CommandInvoke) {
    const body: any = {
      functionName: `${Constants.commandInvokeFunctionName}${environment.api.envSuffix}`,
      run: ctx.getState().supervisorControlPanel.edit.editForm.model.invokeCommands,
    };

    return this.webApiService.request(HttpMethodEnum.POST, `${WebApiRoute.COMMAND_INVOKE}`, body);
  }
  //#endregion Supervisor Control Panel

  // #region shared API
  @LongRunningTask
  @Action(AppDataActions.DataImportValidate)
  DataImportValidate(ctx: StateContext<AppDataStateModel>, action: AppDataActions.DataImportValidate) {
    var excelData = action.excelData;
    var request: DataImportValidateRequest = {
      AwardData: excelData.AwardData,
      KpiDrivingBehaviorData: excelData.KpiDrivingBehaviorData,
      KpiITToolUsageData: [...excelData.evcrComplianceData, ...excelData.aptComplianceData, ...excelData.saftyMeetingComplianceData],
    };

    return this.webApiService.request(HttpMethodEnum.POST, `${WebApiRoute.DATA_IMPORT_VALIDATE}`, request);
  }

  @LongRunningTask
  @Action(AppDataActions.UploadAttachment)
  UploadAttachment(ctx: StateContext<AppDataStateModel>, action: AppDataActions.UploadAttachment) {
    return this.webApiService.uploadFile(action.uploadUrl, action.uploadFile);
  }

  static currentPageFilterSetting(functionName: string) {
    return createSelector([AppDataState], (state: AppDataStateModel) => {
      return state.pageFilterSettings.find((item) => item.functionName === functionName);
    });
  }

  @Selector()
  static currentCommonPageSettings(state: AppDataStateModel) {
    return state.commonPageSettings;
  }

  @Action(AppDataActions.SetPageViewInitialized)
  SetPageViewInitialized(ctx: StateContext<AppDataStateModel>, action: AppDataActions.SetPageViewInitialized) {
    NgxsStoreHelpers.SetPageViewInitialized(ctx, action.functionName);
  }

  @Action(AppDataActions.SetSearchCriteriaWithLastModifyData)
  SetSearchCriteriaWithLastModifyData(ctx: StateContext<AppDataStateModel>, action: AppDataActions.SetSearchCriteriaWithLastModifyData) {
    var profileMe = (<CommonDataStateModel>this.store.selectSnapshot((state) => state.commonDataState)).profileMe;
    StateProcessorFactory.GetProcessor(action.functionName).SetSearchCriteria(ctx, GenerateLastModifySearchCriteria(profileMe.apexId));
  }

  @Action(AppDataActions.SetDefaultSearchCriteria)
  SetDefaultSearchCriteria(ctx: StateContext<AppDataStateModel>, action: AppDataActions.SetDefaultSearchCriteria) {
    StateProcessorFactory.GetProcessor(action.functionName).AttachSearchCriteriaIntoCurrent(ctx, action.searchCriteria);
  }
  //#endregion shared API
}
